import FilenamesMatchResult from '@uploads/models/FilenamesMatchResult';
import { ASSETS_URL, PROJECTS_URL } from 'src/config/urls';
import QueryClientUpdater from '../new_arch/services/QueryClientUpdater';
import {
	UPDATE_ASSET_UPLOAD_PROGRESS,
	UPDATE_ASSET_UPLOAD_STATUS,
	CREATE_FILE_UPLOADS,
	CANCEL_ALL_ASSET_UPLOADS,
	CREATE_FOLDER_UPLOADS,
	UPDATE_FOLDER_UPLOAD_STATUS,
	CLEAR_FINISHED_UPLOADS,
	REMOVE_ASSET_UPLOADS,
	UPDATE_ASSET_UPLOAD_FILE,
	CHECK_ASSET_FILENAMES_MATCHES
} from './types';
import { ITEM_UPLOAD_STATUS } from '../services/DirectFilesUploaderCoordinator';
import { directFilesUploader, directFilesUploaderSubscriber } from '../services/directFilesUploaderInitializer';
import { renameFile, getParsedFileName } from '../lib/fileUtils';

const parseFile = file => renameFile( file, getParsedFileName( { filename: file.name } ) );

export const createFileUploads = ( {
	projectID, folderID, parentUploadID, files, newRounds = [], localFolderName
} ) => {
	const parsedFiles = files.map( parseFile );

	return ( {
		type: CREATE_FILE_UPLOADS,
		payload: directFilesUploader
			.addFiles( parsedFiles )
			.map( ( upload ) => {
				const uploadingRoundForAsset = newRounds.find(
					round => getParsedFileName( { filename: round.filename } ) === upload.file.name
				);

				return ( {
					id: upload.id,
					projectID,
					folderID,
					status: ITEM_UPLOAD_STATUS.WAITING,
					file: upload.file,
					bytesUploaded: 0,
					parentUploadID,
					uploadingRoundForAsset: !!uploadingRoundForAsset,
					assetID: uploadingRoundForAsset?.assetID,
					localFolderName
				} );
			} )
	} );
};

export const createFolderUploads = params => ( {
	type: CREATE_FOLDER_UPLOADS,
	payload: directFilesUploaderSubscriber
		.createFolderUploads( params )
		.map( upload => ( {
			id: upload.id,
			status: upload.status,
			bytesUploaded: 0,
			projectID: upload.projectID,
			organizerID: upload.organizerID,
			organizerType: upload.organizerType,
			directory: upload.directory,
			parentUploadID: upload.parentUploadID
		} ) )
} );

export const checkAssetFilenamesMatches = ( {
	projectID, folderID, files, localFolderName
} ) => ( dispatch, _, api ) => {
	const filenames = files.map( file => getParsedFileName( { filename: file.name } ) );
	const body = { filenames, project_id: projectID, folder_id: folderID };
	if ( localFolderName ) body.local_folder_name = localFolderName;

	return dispatch( {
		type: CHECK_ASSET_FILENAMES_MATCHES,
		promise: api
			.post( `${ASSETS_URL}/match`, body )
			.then( response => FilenamesMatchResult.fromJson( response ) )
	} );
};

export const updateAssetUploadProgress = ( id, { totalBytesUploaded, newBytesUploaded, parentUploadID } ) => ( {
	type: UPDATE_ASSET_UPLOAD_PROGRESS,
	payload: { totalBytesUploaded, newBytesUploaded },
	meta: { id, parentUploadID }
} );

export const updateUploadStatus = ( id, status ) => ( {
	type: UPDATE_ASSET_UPLOAD_STATUS,
	payload: status,
	meta: { id }
} );

export const updateFolderUploadStatus = ( id, status ) => ( {
	type: UPDATE_FOLDER_UPLOAD_STATUS,
	payload: status,
	meta: { id }
} );

export const cancelAssetUpload = ( uploadID ) => {
	directFilesUploader.cancelUpload( uploadID );
	return updateUploadStatus( uploadID, ITEM_UPLOAD_STATUS.ABORTED );
};

export const cancelAllAssetUploads = () => {
	directFilesUploaderSubscriber.cancelAll();
	return { type: CANCEL_ALL_ASSET_UPLOADS };
};

export const clearFinishedUploads = () => ( { type: CLEAR_FINISHED_UPLOADS } );

export const removeAssetUploads = uploadIDs => ( { type: REMOVE_ASSET_UPLOADS, payload: uploadIDs } );

export const updateAssetUploadFile = ( id, file ) => ( {
	type: UPDATE_ASSET_UPLOAD_FILE, payload: file, meta: { id }
} );

export const deleteWatchedFile = uploadID => ( dispatch, getState, api ) => {
	const upload = getState().assetUploads.get( uploadID )

	directFilesUploader.cancelUpload( uploadID )
	dispatch( removeAssetUploads( [ uploadID ] ) )

	if ( !upload?.assetID ) return Promise.resolve()

	const { assetID, projectID } = upload
	return api
		.delete( `${PROJECTS_URL}/${projectID}/assets`, { ids: [ assetID ] } )
		.then( () => {
			QueryClientUpdater.assetsWereDeleted( { assetIDs: [ assetID ], projectID } )
		} )
		.catch( () => {} )
};

export const renameUploadedAsset = ( uploadID, newName ) => ( dispatch, getState, api ) => {
	const upload = getState().assetUploads.get( uploadID )
	if ( !upload?.assetID || upload.status !== ITEM_UPLOAD_STATUS.SUCCEEDED ) return Promise.resolve( false )

	const { assetID, projectID } = upload
	const parsedName = getParsedFileName( { filename: newName } )

	return api
		.patch( `${ASSETS_URL}/${assetID}/update_filename`, { filename: parsedName } )
		.then( ( response ) => {
			QueryClientUpdater.createdOrUpdatedAsset( {
				assetJson: { ...response.response, project_id: projectID },
				projectID,
				parentUploadsData: []
			} )
			return true
		} )
		.catch( () => false )
};
